import { useEffect, useState, useCallback, useMemo } from 'react'
import FormLayout from 'src/app/layout/form-layout/form-layout'
import { FormLayoutProps } from 'src/app/layout/form-layout/form-layout.types'
import useReservationTimeInsertFormHook from './reservation-time-new-form-hook'
import { Grid, TextField, CircularProgress } from '@vapor/material'
import { Controller } from 'react-hook-form'
import { z } from 'zod'
import HicTimeRangePicker from 'src/app/components/hic-date-time-range-picker/hic-time-range-picker'
import { CustomerInput, CustomerType, SimpleCustomerLookupFragment, useCustomerNewMutation, OriginName, ResourceTypeTimeUnitOfMeasurement } from 'src/api/api.generated'
import HicDateRangePicker from 'src/app/components/hic-date-range-picker/hic-date-range-picker'
import { useTranslation } from 'react-i18next'
import useBreakpoints from 'src/app/hooks/breakpoints/use-breakpoints'
import { useTenant } from 'src/app/hooks/context/context.hook'
import { ReservationTimeNewFormData, ResourceTypeProps } from './reservation-time-new-form.types'
import { HICTitle } from 'src/app/components/typography/title'
import { faBuilding, faNoteSticky, faUser, faMoneyBill1 } from '@fortawesome/free-regular-svg-icons'
import { FormItem } from 'hic-components'
import { HiCIcon } from 'src/app/components/icons/icon'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import PersonLookup from 'src/app/components/customer-lookup/person-lookup'
import { CustomerDetailsEdit } from 'src/app/components/customer-details/customer-details-edit'
import CompanyLookup from 'src/app/components/customer-lookup/company-lookup'
import useErrorSnackBar from 'src/app/hooks/error-snack-bar/error-snack-bar'
import useCurrentLocale from 'src/app/hooks/current-locale/use-current-locale'
import ReservationTimeNewToolbar from '../components/reservation-time-new-toolbar'
import { getDayDiff } from 'src/utils/date'
import { useReservationTimeNewCRUD } from './reservation-time-new-crud'
import { reservationTimeNewAsFormData } from './reservation-time-new-utils'
import { LinkToReservation } from '../components/link-to-reservation'
import { ResourceTimeMultiSelect } from '../components/resource-time-multi-select'
import { TotalReservationTime } from '../components/total-reservation-time'
type ReservationTimeNewFormProps = ResourceTypeProps & {
  reservationId?: string
  formLayoutProps: Omit<FormLayoutProps, 'toolbarContents' | 'title' | 'toolbar'>
  propertyId: string
  onSubmitAndOpenReservation?: (reservationId: string) => void
  onCancel: () => void
  resourceTypeId: string
  onSubmitComplete: () => void
  resourceId?: string
  start?: Date
  end?: Date
  from?: Date
  to?: Date
}

export const ReservationTimeNewForm = (props: ReservationTimeNewFormProps) => {
  const {
    formLayoutProps,
    onSubmitAndOpenReservation,
    propertyId,
    resourceTypeId,
    resourceId,
    name,
    unitOfMeasurement,
    activity,
    onCancel,
    rackRate,
    minRate,
    start,
    end,
    from,
    to,
    chargeCategoryId,
  } = props
  const { id: tenantId, currency } = useTenant()
  const { t } = useTranslation()
  const { sm } = useBreakpoints()
  const showErrorSnackBar = useErrorSnackBar()
  const { hicLanguage } = useCurrentLocale()

  const [loading, setLoading] = useState(false)
  const [customerPersonId, setCustomerPersonId] = useState<string | undefined>()
  const [customerCompanyId, setCustomerCompanyId] = useState<string | undefined>()
  const [noteField, setNoteField] = useState('')
  const [currentGuest, setCurrentGuest] = useState<SimpleCustomerLookupFragment | null>(null)
  const [currentCompany, setCurrentCompany] = useState<SimpleCustomerLookupFragment | null>(null)
  const [reservationId, setReservationId] = useState<string | undefined>(props.reservationId)

  const { loading: loadingCRUD, originsResult, upsertReservationTime, reservationStatesResult, baseRateQuery, reservation } = useReservationTimeNewCRUD(tenantId, propertyId, reservationId)

  const { schema, control, onFormSubmit, setValue, getValues, errors, watch, onFormSubmitAndOpenReservation } = useReservationTimeInsertFormHook({
    onSubmit: async (data) => {
      const originId = originsResult?.origins?.find((o) => o.name === OriginName.PMS)?.id ?? originsResult?.origins?.[0]?.id
      const confirmedStateId = reservationStatesResult?.reservationStates?.find((state) => state.confirmed === true)?.id
      const baseRate = baseRateQuery?.rates?.[0]
      if (confirmedStateId && originId && baseRate) {
        const formData: ReservationTimeNewFormData = reservationTimeNewAsFormData({
          tenantId,
          propertyId,
          resourceTypeId,
          data,
          originId,
          confirmedStateId,
          chargeCategoryId,
          rateId: baseRate.id,
        })
        const result = await upsertReservationTime(formData, reservationId, undefined, undefined, props.onSubmitComplete)
        return result
      }
    },
    openReservation: onSubmitAndOpenReservation,
  })

  type ReservationTime = z.TypeOf<typeof schema>

  const formValues = watch()

  useEffect(() => {
    console.group('formValues')
    console.log(formValues)
    console.groupEnd()
  }, [formValues])

  useEffect(() => {
    setValue('reservationDate.checkIn', start || new Date())
    setValue('reservationDate.checkOut', end || new Date())
    if (from) setValue('reservationTime.start', from)
    if (to) setValue('reservationTime.end', to)
    if (resourceId) setValue('resourceIds', [resourceId])
  }, [])

  useEffect(() => {
    const reservationLinked = reservation?.reservations?.[0]
    if (reservationLinked) {
      setCustomerPersonId(reservationLinked.reference.mainGuestId)
      setCustomerCompanyId(reservationLinked.reference.customerCompanyId ?? undefined)
    }
  }, [reservation])

  useEffect(() => {
    setValue('notes', noteField)
  }, [noteField])

  const [createCustomerMutation, { loading: createCustomerLoading }] = useCustomerNewMutation()

  useEffect(() => {
    setCustomerCompanyId(watch('customerCompanyId') ?? undefined)
  }, [watch('customerCompanyId')])

  useEffect(() => {
    setCustomerPersonId(watch('customerPersonId'))
  }, [watch('customerPersonId')])

  const createNewCustomer = useCallback(
    async (name: { lastName: string; firstName: string }, customerType: CustomerType) => {
      const input: CustomerInput = {
        tenantId: tenantId,
        language: hicLanguage,
        lastName: name.lastName,
        firstName: name.firstName,
        name: `${name.lastName} ${name.firstName}`,
        type: customerType,
      }
      try {
        setLoading(true)
        const result = await createCustomerMutation({ variables: { input: input } })
        const customer = result.data?.createCustomer
        if (!customer) {
          return { type: 'FAIL', errors: result.errors }
        }
        return { type: 'SUCCESS', customer }
      } catch (e) {
        return { type: 'FAIL' }
      } finally {
        setLoading(false)
      }
    },
    [tenantId, createCustomerMutation],
  )

  const onBlurHandlerPersonLookup = async (name: { lastName: string; firstName: string }) => {
    const createdCustomer = await createNewCustomer(name, 'PERSON')
    if (createdCustomer.type === 'ERROR') {
      showErrorSnackBar(createdCustomer.errors)
      setValue('customerPersonId', '')
    } else {
      setCurrentGuest(createdCustomer.customer || null)
      setValue('customerPersonId', createdCustomer.customer?.id || '')
    }
  }
  const onBlurHandlerCompanyLookup = async (name: { lastName: string; firstName: string }) => {
    const createdCustomer = await createNewCustomer(name, 'COMPANY')
    if (createdCustomer.type === 'ERROR') {
      showErrorSnackBar(createdCustomer.errors)
    } else {
      setCurrentCompany(createdCustomer.customer || null)
      if (createdCustomer.customer?.id) {
        setValue('customerCompanyId', createdCustomer.customer?.id) // FIXME: risolvere il problema di disassociazione dell'anagrafica.
      }
    }
  }

  useEffect(() => {
    setLoading(loadingCRUD || createCustomerLoading)
  }, [loadingCRUD, createCustomerLoading])

  const onReservationSelect = (reservationId: string | undefined) => {
    console.log(reservationId)
    setReservationId(reservationId)
  }

  const resourcesSelected = useMemo(() => {
    return watch('resourceIds')?.length ?? 0
  }, [watch('resourceIds')])

  const days = useMemo(() => {
    if (watch('reservationDate.checkIn') && watch('reservationDate.checkOut')) {
      const start = watch('reservationDate.checkIn')
      const end = watch('reservationDate.checkOut')
      return getDayDiff(start, end)
    }
  }, [watch('reservationDate'), watch('reservationDate.checkIn'), watch('reservationDate.checkOut')])

  const reservationTime = useMemo((): { from: Date; to: Date } | undefined => {
    const from = watch('reservationTime.start')
    const to = watch('reservationTime.end')
    if (from && to) return { from, to }
  }, [watch('reservationTime')])

  return (
    <>
      {loading && <CircularProgress />}
      <FormLayout //
        title={t('pms.booking.reservation.reservation_time_creation.title', { timeResourceType: name })}
        {...formLayoutProps}
        appearance={{ ...formLayoutProps.appearance }}
        toolbar={
          <ReservationTimeNewToolbar
            confirmButton={{
              disabled: loading,
              clickHandler: onFormSubmit,
              text: t('pms.booking.reservation_new.save_and_close'),
            }}
            cancelButton={{
              disabled: loading,
              clickHandler: onCancel,
            }}
            confirmAndOpenButton={
              props.onSubmitAndOpenReservation
                ? {
                    disabled: loading,
                    clickHandler: async () => {
                      await onFormSubmitAndOpenReservation(getValues())
                    },
                  }
                : undefined
            }
            dateRange={{
              checkIn: watch('reservationDate.checkIn'),
              checkOut: watch('reservationDate.checkOut'),
            }}
            resources={resourcesSelected}
            currency={currency}
            averageDailyPrice={rackRate}
            totalAccomodationPrice={days !== undefined && resourcesSelected && watch('manualPrice') ? resourcesSelected * (days + 1) * watch('manualPrice') : 0}
          />
        }
      >
        <Grid container spacing={1}>
          {/* <details style={{ color: 'red', fontSize: 12 }}>
            <summary>debug</summary>
            {JSON.stringify(watch())}
          </details> */}
          <Grid item xs={4}>
            <Controller<ReservationTime, 'reservationDate'>
              name={'reservationDate'}
              control={control}
              render={({ field: { value, onChange } }) => (
                <HicDateRangePicker
                  fieldSmall
                  minDate={new Date()}
                  value={value ? [value.checkIn, value.checkOut] : start && end ? [start, end] : undefined}
                  onChange={(newValue) => {
                    const dateRange = { checkIn: newValue[0], checkOut: newValue[1] }
                    onChange(dateRange)
                  }}
                  start={{
                    startLabel: t('pms.booking.reservation_time_new.check_in'),
                    startDateError: errors.reservationDate?.checkIn ? t('commons.required_field') : undefined,
                    maxWidth: sm ? '130px' : undefined,
                  }}
                  end={{
                    endLabel: t('pms.booking.reservation_time_new.check_out'),
                    endDateError: errors.reservationDate?.checkOut ? t('commons.required_field') : undefined,
                    maxWidth: sm ? '130px' : undefined,
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={4}>
            <Controller<ReservationTime, 'reservationTime'>
              name={'reservationTime'}
              control={control}
              render={({ field: { value, onChange } }) => {
                return (
                  <HicTimeRangePicker
                    fieldSmall
                    maxWidth={sm ? '130px' : undefined}
                    value={
                      value ||
                      (from && to
                        ? { start: from, end: to }
                        : unitOfMeasurement === ResourceTypeTimeUnitOfMeasurement.DAY
                        ? { start: new Date(1970, 0, 1, 0, 0), end: new Date(1970, 0, 1, 23, 59) }
                        : { start: undefined, end: undefined })
                    }
                    onChange={onChange}
                    startProps={{
                      startLabel: t('pms.commons.from'),
                      startDateError: errors.reservationTime?.start?.message,
                    }}
                    endProps={{
                      endLabel: t('pms.commons.to'),
                      endDateError: errors.reservationTime?.end?.message,
                    }}
                    timeUnitOfMeasurement={unitOfMeasurement}
                    activity={activity}
                    errors={errors.reservationTime ? true : false}
                  />
                )
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller<ReservationTime, 'resourceIds'>
              name={'resourceIds'}
              control={control}
              render={({ field: { value, onChange } }) => (
                <FormItem
                  errorMessage={errors.resourceIds && errors.resourceIds.length > 0 ? t('commons.required_field') : ''}
                  fieldLabel={t('pms.booking.reservation_time_new.time_resources')}
                  fullWidth
                >
                  <ResourceTimeMultiSelect
                    size="small"
                    fullWidth
                    value={value}
                    propertyId={propertyId}
                    resourceTypeId={resourceTypeId}
                    start={watch('reservationDate.checkIn')}
                    end={watch('reservationDate.checkOut')}
                    time={{ from: watch('reservationTime.start'), to: watch('reservationTime.end') }}
                    onChange={onChange}
                  />
                </FormItem>
              )}
            />
          </Grid>
          <Grid item xs={12} mt={2}>
            {!props.reservationId && <LinkToReservation title={t('pms.booking.reservation_time_new.link_to_reservation')} propertyId={propertyId} onSelection={onReservationSelect} />}
          </Grid>
          {reservationTime && resourcesSelected > 0 && (
            <Grid item xs={12} mt={2}>
              <HICTitle title={t('pms.booking.reservation_new_time.price')} faIcon={faMoneyBill1} />
              <Controller<ReservationTime, 'manualPrice'>
                name="manualPrice"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <TotalReservationTime //
                    rackRate={rackRate}
                    minRate={minRate}
                    days={days !== undefined ? days + 1 : 1}
                    quantity={resourcesSelected}
                    activity={activity}
                    reservationTime={reservationTime}
                    unitOfMeasurement={unitOfMeasurement}
                    value={value}
                    onChange={onChange}
                  />
                )}
              />
            </Grid>
          )}
          {!reservationId && (
            <Grid item xs={12} mt={2}>
              <HICTitle title={t('pms.booking.reservation_new.booking_notes_title')} faIcon={faNoteSticky} />

              <Controller<ReservationTime, 'notes'>
                name={'notes'}
                control={control}
                render={() => (
                  <FormItem errorMessage={errors.notes?.message} fullWidth>
                    <TextField
                      size="small"
                      value={noteField}
                      onChange={(event) => setNoteField(event.target.value)}
                      multiline
                      placeholder={t('pms.booking.reservation_new.booking_notes_placeholder')}
                    />
                  </FormItem>
                )}
              />
            </Grid>
          )}

          <Grid item xs={12} mt={2}>
            <HICTitle title={t('pms.booking.reservation_new.reservant')} faIcon={faUser} />
            <Grid container rowSpacing={1} columnSpacing={2}>
              <Grid item xs={12} sm={6}>
                <Controller<ReservationTime, 'customerPersonId'> //
                  name={'customerPersonId'}
                  control={control}
                  render={({ field }) => (
                    <div id={'customerPersonId'}>
                      <FormItem
                        errorMessage={errors?.customerPersonId?.message}
                        fullWidth
                        fieldLabel={t('pms.booking.reservation_new.person')}
                        fieldIcon={
                          <HiCIcon sx={{ pr: 1 }}>
                            <FontAwesomeIcon icon={faUser} />
                          </HiCIcon>
                        }
                      >
                        <PersonLookup
                          size="small"
                          disabled={loading || reservationId !== undefined}
                          value={customerPersonId ? { id: customerPersonId } : null}
                          onChange={(value) => {
                            field.onChange(value?.id || null)
                            setCurrentGuest(value)
                            const mailAddress = value?.mailContacts?.find((m) => m.isDefault)?.value || undefined
                            if (mailAddress) setValue('customerEmailAddress', mailAddress)

                            if (value?.relatedCompanyId) {
                              setValue('customerCompanyId', value?.relatedCompanyId)
                            }
                          }}
                          minSearchParameterLenght={2}
                          onBlur={(value) => {
                            field.onBlur()
                            onBlurHandlerPersonLookup(value)
                          }}
                          updateAction
                          createAction
                          showEmail={true}
                          showPhoneNumber={true}
                          showTags={true}
                          showRelatedCompany={true}
                        />
                      </FormItem>
                      {currentGuest !== null ? (
                        <CustomerDetailsEdit
                          value={currentGuest}
                          type={CustomerType.PERSON}
                          onChange={(value) => {
                            setCurrentGuest(value)
                            // if (getValues().sendEmailAfterInsert) {
                            const mailAddress = value?.mailContacts?.find((m) => m.isDefault)?.value || undefined
                            if (mailAddress) setValue('customerEmailAddress', mailAddress)
                            // }
                            if (value === null) setValue('customerPersonId', '')
                          }}
                          onErrors={(e) => {
                            if (e?.mailContact?.message) setValue('customerEmailAddress', null)
                          }}
                          // requireEmailAddress={getValues().sendEmailAfterInsert}
                        />
                      ) : undefined}
                    </div>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller<ReservationTime, 'customerCompanyId'> //
                  name={'customerCompanyId'}
                  control={control}
                  render={({ field }) => (
                    <>
                      <FormItem
                        errorMessage={errors?.customerCompanyId?.message}
                        fullWidth
                        fieldLabel={t('pms.booking.reservation_new.company')}
                        fieldIcon={
                          <HiCIcon sx={{ pr: 1 }}>
                            <FontAwesomeIcon icon={faBuilding} />
                          </HiCIcon>
                        }
                      >
                        <CompanyLookup //
                          size="small"
                          error={errors?.customerCompanyId ? true : false}
                          value={customerCompanyId ? { id: customerCompanyId } : null}
                          onChange={(value) => {
                            field.onChange(value?.id || null)
                            setCurrentCompany(value)
                          }}
                          disabled={loading || reservationId !== undefined}
                          // clearOnBlur={false}
                          onBlur={onBlurHandlerCompanyLookup}
                          createAction
                          updateAction
                          showTags={true}
                        />
                      </FormItem>
                      {currentCompany !== null ? (
                        <CustomerDetailsEdit
                          value={currentCompany}
                          type={CustomerType.COMPANY}
                          onChange={(value) => {
                            setCurrentCompany(value)
                            if (value === null) setValue('customerCompanyId', '')
                          }}
                        />
                      ) : undefined}
                    </>
                  )}
                />
              </Grid>
              {/* <Grid item container xs={12} style={{ paddingLeft: '16px' }} spacing={0}>
                <Controller<ReservationTime, 'sendEmailAfterInsert'> //
                  name={'sendEmailAfterInsert'}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <FormControlLabel //
                      control={<Checkbox defaultChecked={true} value={value} onChange={onChange} />}
                      label={`${t('pms.booking.reservation_new.send_email_after_inser')}`}
                    />
                  )}
                />
              </Grid> */}
            </Grid>
          </Grid>
        </Grid>
      </FormLayout>
    </>
  )
}

export default ReservationTimeNewForm
